/**
 * The right-click menu on a row or a bar.
 *
 * Every action on it is also on the toolbar or a key, so nothing here is the only way to
 * do anything. It exists because the question "what can I do with this task" is asked
 * with the pointer already on the task, and the answer should arrive there rather than
 * in a toolbar forty pixels away. The same menu on the grid row and the chart bar,
 * because they are the same task.
 */
import { useEffect, useRef, type ReactNode } from 'react'
import clsx from 'clsx'
import { Portal } from '../../components/overlay'

export type MenuEntry =
  | { kind: 'item'; label: string; icon?: ReactNode; shortcut?: string; danger?: boolean; disabled?: boolean; onSelect: () => void }
  | { kind: 'divider' }
  | { kind: 'heading'; label: string }
  /** A tick that stays open when pressed, for lists where people tick several at once. */
  | { kind: 'check'; label: string; checked: boolean; disabled?: boolean; onToggle: () => void }

export function ContextMenu({
  at,
  entries,
  onClose,
}: {
  at: { x: number; y: number }
  entries: MenuEntry[]
  onClose: () => void
}) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const down = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose()
    }
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    // Scrolling the page under the menu closes it; scrolling a long menu itself must not.
    const scrolled = (e: Event) => {
      if (!ref.current?.contains(e.target as Node)) onClose()
    }
    // Attached a tick later, not now. React flushes the effects of a right-click before
    // that right-click has finished bubbling, so a listener attached here directly
    // hears the very event that opened the menu and closes it in the same breath.
    const timer = window.setTimeout(() => {
      // A second right-click elsewhere opens a fresh menu there, so the old one goes.
      document.addEventListener('mousedown', down)
      document.addEventListener('contextmenu', down)
      document.addEventListener('keydown', key)
      window.addEventListener('scroll', scrolled, true)
    }, 0)
    return () => {
      window.clearTimeout(timer)
      document.removeEventListener('mousedown', down)
      document.removeEventListener('contextmenu', down)
      document.removeEventListener('keydown', key)
      window.removeEventListener('scroll', scrolled, true)
    }
  }, [onClose])

  // Keep it on the screen: open to the left of the pointer near the right edge, and
  // above it near the bottom.
  const width = 240
  const height = Math.min(entries.length * 30 + 12, window.innerHeight * 0.8)
  const left = Math.min(at.x, window.innerWidth - width - 8)
  const top = Math.min(at.y, window.innerHeight - height - 8)

  return (
    <Portal>
      <div
        ref={ref}
        role="menu"
        className="card fixed z-[60] max-h-[80vh] overflow-y-auto py-1 shadow-xl"
        style={{ left, top, width }}
        onContextMenu={(e) => e.preventDefault()}
      >
        {entries.map((entry, i) => {
          if (entry.kind === 'divider') {
            return <div key={i} className="my-1 border-t border-[var(--color-rule)]" />
          }
          if (entry.kind === 'heading') {
            return (
              <div key={i} className="px-3 pt-1.5 pb-0.5 text-[10px] font-semibold tracking-wider text-[var(--color-ink-faint)] uppercase">
                {entry.label}
              </div>
            )
          }
          if (entry.kind === 'check') {
            return (
              <button
                key={i}
                type="button"
                role="menuitemcheckbox"
                aria-checked={entry.checked}
                disabled={entry.disabled}
                onClick={() => entry.onToggle()}
                className={clsx(
                  'flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] text-[var(--color-ink)]',
                  'disabled:cursor-not-allowed disabled:opacity-40',
                  !entry.disabled && 'hover:bg-[var(--color-surface-sunk)]',
                )}
              >
                <span
                  aria-hidden
                  className={clsx(
                    'flex size-4 shrink-0 items-center justify-center rounded-[3px] border text-[10px] leading-none',
                    entry.checked
                      ? 'border-[var(--color-accent)] bg-[var(--color-accent)] text-[var(--color-accent-contrast)]'
                      : 'border-[var(--color-rule-strong)]',
                  )}
                >
                  {entry.checked ? '✓' : ''}
                </span>
                <span className="min-w-0 flex-1 truncate">{entry.label}</span>
              </button>
            )
          }
          return (
            <button
              key={i}
              type="button"
              role="menuitem"
              disabled={entry.disabled}
              onClick={() => {
                entry.onSelect()
                onClose()
              }}
              className={clsx(
                'flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px]',
                'disabled:cursor-not-allowed disabled:opacity-40',
                entry.danger ? 'text-[var(--color-danger)]' : 'text-[var(--color-ink)]',
                !entry.disabled && 'hover:bg-[var(--color-surface-sunk)]',
              )}
            >
              <span className="flex w-4 shrink-0 items-center justify-center text-[var(--color-ink-faint)]">
                {entry.icon}
              </span>
              <span className="min-w-0 flex-1 truncate">{entry.label}</span>
              {entry.shortcut && (
                <span className="shrink-0 font-mono text-[10px] text-[var(--color-ink-faint)]">{entry.shortcut}</span>
              )}
            </button>
          )
        })}
      </div>
    </Portal>
  )
}
